import type { Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { forgetOnFinalClientError } from '../http/idempotency.ts'

export type Settlement = Schemas['LedgerRelationshipResponse']['settlement']
export type ProposalStatus = Schemas['LedgerRelationshipProposalResponse']['status']

export interface Relationship {
  readonly ledgerAId: string
  readonly ledgerBId: string
  readonly settlement: Settlement
}

export interface RelationshipProposal {
  readonly id: string
  readonly proposingLedgerId: string
  readonly targetLedgerId: string
  readonly settlement: Settlement
  readonly status: ProposalStatus
}

/** A proposal this ledger sent; the wire carries the target's id but not its name. */
export interface OutgoingRelationshipProposal {
  readonly id: string
  readonly targetLedgerId: string
  readonly settlement: Settlement
  readonly status: ProposalStatus
  readonly createdAt: string
}

/** A connected counterparty, with the name the other ledger chose for itself. */
export interface RelationshipListing {
  readonly otherLedgerId: string
  readonly otherLedgerName: string
  readonly settlement: Settlement
}

export interface ProposeRelationshipInput {
  readonly targetLedgerCode: string
  readonly settlement: Settlement
}

export interface ShareCodePreview {
  readonly name: string
}

export const relationshipKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'relationships'),
  proposals: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'relationship-proposals'),
  outgoing: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'relationship-proposals', 'outgoing'),
  /** A proposal or a relationship change is visible from both ledgers it names. */
  invalidatedByWrite: (ledgerAId: string, ledgerBId: string): Invalidations => [
    ledgerKey(ledgerAId, 'relationships'),
    ledgerKey(ledgerAId, 'relationship-proposals'),
    ledgerKey(ledgerBId, 'relationships'),
    ledgerKey(ledgerBId, 'relationship-proposals'),
  ],
} as const

export interface RelationshipsRepository {
  get(ledgerId: string, otherLedgerId: string, signal?: AbortSignal): Promise<Relationship>
  replace(ledgerId: string, otherLedgerId: string, settlement: Settlement): Promise<Relationship>
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly RelationshipListing[]>
  listProposals(ledgerId: string, signal?: AbortSignal): Promise<readonly RelationshipProposal[]>
  listOutgoingProposals(
    ledgerId: string,
    signal?: AbortSignal,
  ): Promise<readonly OutgoingRelationshipProposal[]>
  proposeRelationship(ledgerId: string, input: ProposeRelationshipInput): Promise<RelationshipProposal>
  /** Resolves a ledger code to the target ledger's name only. Unknown and archived codes are one not-found. */
  previewShareCode(code: string, signal?: AbortSignal): Promise<ShareCodePreview>
  /** Owner-only. Returns the new code; the previous one stops resolving at once. */
  rotateShareCode(ledgerId: string): Promise<string>
  accept(ledgerId: string, proposalId: string): Promise<Relationship>
  /** `204 No Content` on the wire - the contract carries no updated proposal back. */
  decline(ledgerId: string, proposalId: string): Promise<void>
}

export function createRelationshipsRepository(client: TalerClient): RelationshipsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/relationships`
  const proposalsBase = (ledgerId: string) =>
    `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/relationship-proposals`

  return {
    async get(ledgerId, otherLedgerId, signal) {
      return toRelationship(
        await client.call<Schemas['LedgerRelationshipResponse']>({
          method: 'GET',
          path: `${base(ledgerId)}/${encodeURIComponent(otherLedgerId)}`,
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async replace(ledgerId, otherLedgerId, settlement) {
      const body: Schemas['SetRelationshipRequest'] = { settlement }
      return toRelationship(
        await client.call<Schemas['LedgerRelationshipResponse'], Schemas['SetRelationshipRequest']>({
          method: 'PUT',
          path: `${base(ledgerId)}/${encodeURIComponent(otherLedgerId)}`,
          body,
        }),
      )
    },

    async list(ledgerId, signal) {
      const response = await client.call<Schemas['LedgerRelationshipListingResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map((item) => ({
        otherLedgerId: item.otherLedgerId,
        otherLedgerName: item.otherLedgerName,
        settlement: item.settlement,
      }))
    },

    async listProposals(ledgerId, signal) {
      const response = await client.call<Schemas['LedgerRelationshipProposalResponse'][]>({
        method: 'GET',
        path: proposalsBase(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map(toProposal)
    },

    async listOutgoingProposals(ledgerId, signal) {
      const response = await client.call<Schemas['OutgoingRelationshipProposalResponse'][]>({
        method: 'GET',
        path: `${proposalsBase(ledgerId)}/outgoing`,
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map((item) => ({
        id: item.id,
        targetLedgerId: item.targetLedgerId,
        settlement: item.settlement,
        status: item.status,
        createdAt: item.createdAt,
      }))
    },

    async proposeRelationship(ledgerId, input) {
      const body: Schemas['ProposeRelationshipRequest'] = {
        targetLedgerCode: input.targetLedgerCode,
        settlement: input.settlement,
      }
      const path = proposalsBase(ledgerId)
      const payload = { method: 'POST' as const, path, query: null, body }
      let response: Schemas['LedgerRelationshipProposalResponse']
      try {
        response = await client.call<
          Schemas['LedgerRelationshipProposalResponse'],
          Schemas['ProposeRelationshipRequest']
        >({ method: 'POST', path, body })
      } catch (error) {
        forgetOnFinalClientError(client.idempotencyKeys, payload, error)
        throw error
      }
      // A new proposal each time: an identical code and settlement later must not replay this one.
      client.idempotencyKeys.forget(payload)
      return toProposal(response)
    },

    async previewShareCode(code, signal) {
      const response = await client.call<Schemas['ShareCodePreviewResponse']>({
        method: 'GET',
        path: `/api/v1/ledger-codes/${encodeURIComponent(code)}`,
        ...(signal === undefined ? {} : { signal }),
      })
      return { name: response.name }
    },

    async rotateShareCode(ledgerId) {
      const path = `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/share-code/rotate`
      const response = await client.call<Schemas['ShareCodeResponse'], Record<string, never>>({
        method: 'POST',
        path,
        body: {},
      })
      client.idempotencyKeys.forget({ method: 'POST', path, query: null, body: {} })
      return response.shareCode
    },

    async accept(ledgerId, proposalId) {
      return toRelationship(
        await client.call<Schemas['LedgerRelationshipResponse'], Record<string, never>>({
          method: 'POST',
          path: `${proposalsBase(ledgerId)}/${encodeURIComponent(proposalId)}/accept`,
          body: {},
        }),
      )
    },

    async decline(ledgerId, proposalId) {
      await client.call<void, Record<string, never>>({
        method: 'POST',
        path: `${proposalsBase(ledgerId)}/${encodeURIComponent(proposalId)}/decline`,
        body: {},
      })
    },
  }
}

function toRelationship(response: Schemas['LedgerRelationshipResponse']): Relationship {
  return {
    ledgerAId: response.ledgerAId,
    ledgerBId: response.ledgerBId,
    settlement: response.settlement,
  }
}

function toProposal(response: Schemas['LedgerRelationshipProposalResponse']): RelationshipProposal {
  return {
    id: response.id,
    proposingLedgerId: response.proposingLedgerId,
    targetLedgerId: response.targetLedgerId,
    settlement: response.settlement,
    status: response.status,
  }
}
